import { atom } from "jotai";
import { unwrap } from "jotai/utils";

import type { Atom } from "jotai";

export type Loadable<Value> =
  | { state: "loading" }
  | { state: "hasError"; error: unknown }
  | { state: "hasData"; data: Awaited<Value> };

const memoised = new WeakMap<Atom<unknown>, Atom<Loadable<unknown>>>();

export function loadable<Value>(anAtom: Atom<Value>): Atom<Loadable<Value>> {
  const cached = memoised.get(anAtom);
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- memoised is keyed by the source atom, so this entry was built by loadable() for this same Atom<Value>
  if (cached) return cached as Atom<Loadable<Value>>;

  // Private per source atom, so it can never collide with a resolved value.
  const LOADING: Loadable<Value> = { state: "loading" };

  // `unwrap` passes a synchronous source straight through, so sync atoms never
  // flash `loading`; it returns the fallback whenever a promise is in flight,
  // including a refetch after a dependency changes.
  const unwrapped = unwrap(anAtom, () => LOADING);

  const loadableAtom = atom((get): Loadable<Value> => {
    try {
      const data = get(unwrapped);
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- unwrap() yields Awaited<Value> or the LOADING fallback, and LOADING is excluded here
      return data === LOADING ? LOADING : { state: "hasData", data: data as Awaited<Value> };
    } catch (error) {
      return { state: "hasError", error };
    }
  });

  memoised.set(anAtom, loadableAtom);
  return loadableAtom;
}
